import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Stocker le mot de passe admin dans le state
  const [adminPassword, setAdminPassword] = useState(null);

  const updateUserData = async (user, initialData = null) => {
    try {
      
      // Si nous avons des données initiales (création de compte), les utiliser directement
      if (initialData) {
        setCurrentUser(user);
        setUserRole(initialData.role);
        setUserData(initialData);
        return initialData;
      }

      // Sinon, vérifier dans les collections
      
      // Vérifier d'abord dans la collection superadmin
      const superAdminDoc = await getDoc(doc(db, 'superadmins', user.uid));

      if (superAdminDoc.exists()) {
        const superAdminData = superAdminDoc.data();
        setCurrentUser(user);
        setUserRole('superAdmin');
        setUserData({ ...superAdminData, role: 'superAdmin' });
        return { role: 'superAdmin', ...superAdminData };
      }

      // Vérifier dans la collection users
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userDocData = userDoc.data();
        setCurrentUser(user);
        setUserRole(userDocData.role);
        setUserData(userDocData);
        return userDocData;
      }

      // Si l'utilisateur n'est pas trouvé, mais qu'il vient d'être créé
      // on attend un peu et on réessaie une fois
      if (!userDoc.exists() && initialData === null) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const retryDoc = await getDoc(doc(db, 'users', user.uid));
        if (retryDoc.exists()) {
          const retryData = retryDoc.data();
          setCurrentUser(user);
          setUserRole(retryData.role);
          setUserData(retryData);
          return retryData;
        }
      }

      console.error('Utilisateur non trouvé dans les collections');
      throw new Error('Compte utilisateur non trouvé');
    } catch (error) {
      console.error('=== Erreur dans updateUserData ===');
      console.error('Type d\'erreur:', error.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);
      setCurrentUser(null);
      setUserRole(null);
      setUserData(null);
      throw error;
    }
  };

  const createUser = async (email, password, userData) => {
    let authUser = null;
    const adminEmail = currentUser?.email;
    const currentAdminPassword = adminPassword;
    
    try {

      // 1. Validation des données
      if (!email || !password) {
        console.error('❌ Email/Password manquant:', {
          email: !!email,
          password: !!password,
          passwordLength: password?.length
        });
        throw new Error('Email et mot de passe requis');
      }

      if (!userData.role || !userData.department || !userData.establishmentId) {
        console.error('❌ Données obligatoires manquantes:', {
          role: userData.role,
          department: userData.department,
          establishmentId: userData.establishmentId
        });
        throw new Error('Role, département et ID établissement sont requis');
      }

      // Pour le personnel, vérifier service et grade
      if (userData.role === 'personnel' && (!userData.service || !userData.grade)) {
        console.error('❌ Données spécifiques au personnel manquantes:', {
          service: userData.service,
          grade: userData.grade
        });
        throw new Error('Service et grade requis pour le personnel');
      }


      // 2. Créer le compte Firebase Auth
      try {
        // Ne pas se déconnecter si on est admin
        if (!currentUser || currentUser.email !== adminEmail) {
          await signOut(auth);
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        authUser = userCredential.user;

        // Se reconnecter immédiatement en tant qu'admin
        if (adminEmail && currentAdminPassword) {
          await signInWithEmailAndPassword(auth, adminEmail, currentAdminPassword);
        }
      } catch (authError) {
        console.error('❌ ERREUR CRÉATION FIREBASE AUTH:', {
          code: authError.code,
          message: authError.message,
          name: authError.name,
          stack: authError.stack
        });
        throw authError;
      }

      // 3. Créer le document Firestore
      try {
        const userDocRef = doc(db, 'users', authUser.uid);
        
        const userDataWithTimestamp = {
          ...userData,
          uid: authUser.uid,
          email: email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: currentUser?.uid || null
        };

        await setDoc(userDocRef, userDataWithTimestamp);

        // Vérifier la création
        const checkDoc = await getDoc(userDocRef);
        if (!checkDoc.exists()) {
          console.error('❌ Document non trouvé après création');
          throw new Error('Le document utilisateur n\'a pas été créé dans Firestore');
        }

        return userDataWithTimestamp;
      } catch (firestoreError) {
        console.error('❌ ERREUR CRÉATION FIRESTORE:', firestoreError);
        // Si erreur Firestore, supprimer le compte Auth
        if (authUser) {
          try {
            await authUser.delete();
          } catch (deleteError) {
            console.error('❌ Erreur suppression compte Auth:', deleteError);
          }
        }
        throw firestoreError;
      }
    } catch (error) {
      console.error('❌ ERREUR GLOBALE:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Attendre que les données soient chargées
      const userData = await updateUserData(userCredential.user);
      
      return userData;
    } catch (error) {
      console.error('=== Erreur dans login ===');
      console.error('Type d\'erreur:', error.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userData = await updateUserData(user);
        } else {
          setCurrentUser(null);
          setUserRole(null);
          setUserData(null);
        }
      } catch (error) {
        console.error('Erreur dans onAuthStateChanged:', error);
        // Ne pas déconnecter l'utilisateur en cas d'erreur
        // Attendre la prochaine tentative
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userData,
    login,
    logout,
    createUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
